/**
 * 海拔查询（中心点 / 指针共用）
 * - 优先 Open-Meteo，回退 Open-Elevation（浏览器直连，不经自家后端）
 * - 按坐标 key 缓存，避免不同点互相覆盖错误高程
 * - 全局限流，降低外部 API 压力
 */

const cache = new Map(); // key -> { elev, at }
const inFlightByKey = new Map();
let lastFetchAt = 0;
let lastFailedAt = 0;

const MIN_INTERVAL_MS = 2000;
const MAX_CACHE = 48;
const FAIL_COOLDOWN_MS = MIN_INTERVAL_MS * 6;

function cacheSet(key, elev) {
  if (cache.size >= MAX_CACHE) {
    const oldest = cache.keys().next().value;
    if (oldest != null) cache.delete(oldest);
  }
  cache.set(key, { elev, at: Date.now() });
}

async function lookupOpenMeteo(lng, lat, fetchImpl) {
  const url = `https://api.open-meteo.com/v1/elevation?latitude=${lat}&longitude=${lng}`;
  const res = await fetchImpl(url);
  if (!res.ok) throw new Error(`open-meteo ${res.status}`);
  const data = await res.json();
  const elev = data?.elevation?.[0];
  if (typeof elev === 'number' && Number.isFinite(elev)) return elev;
  throw new Error('open-meteo empty');
}

async function lookupOpenElevation(lng, lat, fetchImpl) {
  const url = `https://api.open-elevation.com/api/v1/lookup?locations=${lat},${lng}`;
  const res = await fetchImpl(url);
  if (!res.ok) throw new Error(`open-elevation ${res.status}`);
  const data = await res.json();
  const elev = data?.results?.[0]?.elevation;
  if (typeof elev === 'number' && Number.isFinite(elev)) return elev;
  throw new Error('open-elevation empty');
}

/**
 * @param {number} lng WGS84 经度
 * @param {number} lat WGS84 纬度
 * @param {typeof fetch} [fetchImpl]
 * @param {{ force?: boolean }} [options]
 * @returns {Promise<number|null>}
 */
export async function fetchOpenElevation(lng, lat, fetchImpl = fetch, options = {}) {
  const lngN = Number(lng);
  const latN = Number(lat);
  if (!Number.isFinite(lngN) || !Number.isFinite(latN)) return null;

  const key = `${latN.toFixed(4)},${lngN.toFixed(4)}`;
  const now = Date.now();
  const cached = cache.get(key);

  if (!options.force && cached && cached.elev != null) return cached.elev;
  if (inFlightByKey.has(key)) return inFlightByKey.get(key);

  if (!options.force) {
    // 全局限流：未到间隔则返回该点缓存，否则 null（勿返回其它点的高程）
    if (now - lastFetchAt < MIN_INTERVAL_MS) {
      return cached?.elev ?? null;
    }
    if (now - lastFailedAt < FAIL_COOLDOWN_MS && !cached) {
      return null;
    }
  }

  lastFetchAt = now;

  const promise = (async () => {
    try {
      let elev = null;
      try {
        elev = await lookupOpenMeteo(lngN, latN, fetchImpl);
      } catch (_) {
        elev = await lookupOpenElevation(lngN, latN, fetchImpl);
      }
      cacheSet(key, elev);
      return elev;
    } catch (_) {
      lastFailedAt = Date.now();
      return cached?.elev ?? null;
    } finally {
      inFlightByKey.delete(key);
    }
  })();

  inFlightByKey.set(key, promise);
  return promise;
}
